import { useMemo, useState } from 'react';
import { nanoid } from 'nanoid';

import {
  Button,
  Card,
  Form,
  FormProps,
  Table,
  DatePicker,
  Input,
  Modal,
  notification,
  Select
} from 'antd';
import dayjs from 'dayjs';

import 'styles/booking-remind.scss';
import { useTranslation } from 'react-i18next';
import { useGetBookingReminds } from 'hooks/useGetBookingReminds';
import { getColumnBookingReminds } from 'constants/table';
import ExportBookingReminds from 'components/common/export-booking-reminds';
import { PAGE_SIZE_BOOKING_REMINDS } from 'services/api/module/booking.api';
import { useMutation } from '@tanstack/react-query';
import {
  CATEGORIES_BOOKING_REMINDER,
  CategoriesBookingReminderType,
  ParamsEditBookingRemind
} from 'services/api/type/booking.type';
import API from 'services/api/index';
import QUERY_KEYS from 'services/api/queryKeys';
import { queryClient } from 'index';

const { RangePicker } = DatePicker;

function BookingReminds() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [dateRange, setDateRange] = useState<[string, string]>([
    dayjs().format('YYYY-MM-DD'),
    dayjs().format('YYYY-MM-DD')
  ]);
  const [bookingLineId, setBookingLineId] = useState<number | undefined>();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_BOOKING_REMINDS);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [selectedRemind, setSelectedRemind] = useState<any>(null);
  const [category, setCategory] = useState<CategoriesBookingReminderType>(
    CategoriesBookingReminderType.ALL
  );

  const { mutateAsync: mutateEditBookingRemind, isPending: isPendingEditBookingRemind } =
    useMutation({
      mutationFn: ({ remind_id, params }: { remind_id: number; params: ParamsEditBookingRemind }) =>
        API.booking.editBookingRemind(remind_id, params)
    });

  const { mutateAsync: mutateDeleteBookingRemind, isPending: isPendingDeleteBookingRemind } =
    useMutation({
      mutationFn: (remind_id: number) => API.booking.deleteBookingRemind(remind_id)
    });

  const { data, pagination, isLoading } = useGetBookingReminds(
    {
      remind_from: dateRange[0],
      remind_to: dateRange[1],
      booking_line_id: bookingLineId,
      category
    },
    pageSize,
    page
  );

  const dataSource = useMemo(() => {
    if (!data) return [];
    return data;
  }, [data]);

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    if (values.dateRange) {
      const [from, to] = values.dateRange;
      setDateRange([from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')]);
    }
    setCategory(values.category as CategoriesBookingReminderType);
    setBookingLineId(values.booking_line_id ? Number(values.booking_line_id) : undefined);
    setPage(1); // Reset to first page when filters change
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangePageSize = (current: number, size: number) => {
    setPageSize(size);
    setPage(1); // Reset to first page when page size changes
  };

  const handleEditRemind = (record: any) => {
    setSelectedRemind(record);
    editForm.setFieldsValue({
      date: record.date ? dayjs(record.date) : null,
      note: record.note || ''
    });
    setIsEditModalVisible(true);
  };

  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      await mutateEditBookingRemind({
        remind_id: selectedRemind.id,
        params: {
          date: values.date ? values.date.format('YYYY-MM-DD') : '',
          note: values.note
        }
      });
      notification.success({ message: t('common.message.bookingRemindUpdatedSuccessfully') });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_BOOKING_REMINDS] });
      setIsEditModalVisible(false);
      setSelectedRemind(null);
      editForm.resetFields();
    } catch (error: any) {
      console.error('Failed to update booking remind:', error);
      notification.error({ message: error.error || t('common.error') });
    }
  };

  const handleDeleteRemind = async (remind_id: number) => {
    try {
      await mutateDeleteBookingRemind(remind_id);
      notification.success({ message: t('common.message.bookingRemindDeletedSuccessfully') });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_BOOKING_REMINDS] });
      setIsEditModalVisible(false);
      setSelectedRemind(null);
      editForm.resetFields();
    } catch (error: any) {
      console.error('Failed to delete booking remind:', error);
      notification.error({ message: error.error || t('common.error') });
    }
  };

  const handleCancelEdit = () => {
    setIsEditModalVisible(false);
    setSelectedRemind(null);
    editForm.resetFields();
  };

  const columns = getColumnBookingReminds(t, handleEditRemind);

  return (
    <div className="pms-booking-remind">
      <Card title={t('bookingReminds.title')} style={{ width: '100%' }}>
        <Form
          form={form}
          name="booking-remind-filter-form"
          layout="inline"
          initialValues={{
            dateRange: [dayjs(), dayjs()]
          }}
          onFinish={onFinish}
          autoComplete="off"
        >
          <div className="pms-booking-remind__filter-wrapper">
            <div className="pms-booking-remind__filter">
              <Form.Item name="booking_line_id" className="m-0">
                <Input
                  type="number"
                  placeholder={t('bookingReminds.bookingLineId')}
                  style={{ width: 180 }}
                  allowClear
                />
              </Form.Item>

              <Form.Item
                name="dateRange"
                className="m-0"
                rules={[{ required: true, message: t('common.time.selectRangeDate') }]}
              >
                <RangePicker
                  format={'DD-MM-YYYY'}
                  placeholder={[t('common.time.fromDate'), t('common.time.toDate')]}
                />
              </Form.Item>

              <Form.Item name="category" className="m-0">
                <Select placeholder={t('common.categoties.category')} style={{ width: 120 }}>
                  {CATEGORIES_BOOKING_REMINDER.map(item => (
                    <Select.Option key={item.value} value={item.value}>
                      {t(item.label)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Button htmlType="submit" className="ant-btn-secondary btn-submit">
                {t('common.actions.submitButton')}
              </Button>
            </div>
            <ExportBookingReminds
              fileName={`booking-reminds-${dateRange[0]}-${dateRange[1]}-${nanoid()}`}
              filterOptions={{
                remind_from: dateRange[0],
                remind_to: dateRange[1],
                booking_line_id: bookingLineId,
                category: category
              }}
            />
          </div>
        </Form>

        <div className="pms-booking-remind__table">
          <Table
            size="small"
            loading={isLoading}
            rowKey={record => `${record.id}`}
            columns={columns}
            dataSource={dataSource}
            bordered={true}
            pagination={{
              current: page,
              position: ['bottomCenter'],
              total: pagination.total,
              pageSize: pageSize,
              locale: { items_per_page: `/ ${t('common.page')}` },
              showSizeChanger: true,
              showTotal: total => `${total} ${t('common.item')}`,
              onChange: handleChangePage,
              onShowSizeChange: handleChangePageSize
            }}
            locale={{
              emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
            }}
            scroll={{ x: 2000, y: 'calc(100vh - 320px)' }}
          />
        </div>
      </Card>

      <Modal
        title={t('bookingReminds.editRemind')}
        open={isEditModalVisible}
        onCancel={handleCancelEdit}
        width={600}
        destroyOnClose
        footer={[
          <Button
            key="delete"
            danger
            onClick={() => handleDeleteRemind(selectedRemind?.id)}
            loading={isPendingDeleteBookingRemind}
            className="secondary-button"
          >
            {t('common.actions.deleteButton')}
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveEdit}
            loading={isPendingEditBookingRemind}
            className="ant-btn-secondary"
          >
            {t('common.actions.save')}
          </Button>
        ]}
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: '20px' }}>
          <Form.Item
            name="date"
            label={t('bookingReminds.remindDate')}
            rules={[{ required: true, message: t('bookingReminds.remindDatePlaceholder') }]}
          >
            <DatePicker
              format="DD-MM-YYYY"
              style={{ width: '100%' }}
              placeholder={t('bookingReminds.remindDatePlaceholder')}
            />
          </Form.Item>
          <Form.Item
            name="note"
            label={t('bookingReminds.remindContent')}
            rules={[{ required: true, message: t('bookingReminds.remindContentPlaceholder') }]}
          >
            <Input.TextArea
              rows={4}
              placeholder={t('bookingReminds.remindContentPlaceholder')}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default BookingReminds;
